import React, { useEffect, useState } from "react";
import SearchableSelect from "../../../../core/common/SearchableSelect";
import Select from "react-select";
import Swal from "sweetalert2";
import useApi from "../../../../core/customhook/useApi";
import {
  CustomMultilineOption,
  CustomPackageSingleValue,
  packageSelectStyles,
} from "./CustomMultilineOption";
import useProjects from "../../../../core/customhook/useProjects";

interface AssignBulkPackageProps {
  show: boolean;
  handleClose: () => void;
  onSuccess?: () => void;
}

const AssignBulkPackage: React.FC<AssignBulkPackageProps> = ({
  show,
  handleClose,
  onSuccess,
}) => {
  const { projects, loading } = useProjects(); 
  const [filterData, setFilterData] = useState({
    account_name: "",
    program: "",
    dept_loc: "",
    package: "",
  });
 
  const [packageList, setPackageList] = useState([]); 
  
  const getPackages = useApi<any>("/api/v1/admin/packages", {
    method: "GET",
    autoCall: false,
    handleGlobalError: false,
  });

  const attachPackageToDepartment = useApi<any>(
    "/api/v1/admin/packages/assign/project",
    {
      method: "POST",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const res = await getPackages.callApi();

        if (res?.success) {
          setPackageList(res.data);
        }
      } catch {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Unable to load packages.",
        });
      }
    };

    fetchPackages();
  }, []); 

  const handleFieldDataChange = (name: string, value: string | number) => {
    setFilterData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const packageOptions = packageList.map((item: any) => ({
    projectId: item.package_id,
    label: item.package_name,
    name: item.package_name,
    code: item.package_code,
    scope: item.sop,
  }));
 
const handleSave = async () => {
  if (!filterData.account_name || !filterData.package) {
    Swal.fire({
      icon: "warning",
      title: "Required Fields",
      text: "Please complete all required fields before assigning the package.",
      confirmButtonText: "OK",
    });
    return;
  }

  try {
    Swal.fire({
      title: "Assigning Package",
      text: "Please wait while the package is being assigned...",
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: true,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    const departmentPayload = {
      packageId: filterData.package,
      projectId: filterData.account_name,
    };

    const departmentRes =
      await attachPackageToDepartment.callApi(departmentPayload);

    Swal.close();

    const apiMessage =
      departmentRes?.message ||
      departmentRes?.error ||
      departmentRes?.data?.message ||
      departmentRes?.data?.error ||
      "Something went wrong while assigning the package.";

    // Check API response message
    const isAlreadyAssigned =
      apiMessage.toLowerCase().includes("already assigned") ||
      apiMessage.toLowerCase().includes("no new assignments") ||
      apiMessage.toLowerCase().includes("already exists");

    // API failed
    if (!departmentRes?.success) {
      await Swal.fire({
        icon: "error",
        title: "Assignment Failed",
        text: apiMessage,
        confirmButtonText: "OK",
      });

      return;
    }
 
    if (isAlreadyAssigned) {
      await Swal.fire({
        icon: "info",
        title: "Already Assigned",
        text: apiMessage,
        confirmButtonText: "OK",
      });

      return;
    }
 
    await Swal.fire({
      icon: "success",
      title: "Package Assigned Successfully",
      text: apiMessage,
      confirmButtonText: "OK",
    });
 
    await onSuccess?.();

    // Reset form
    setFilterData({
      account_name: "",
      program: "",
      dept_loc: "",
      package: "",
    });

    handleClose();
  } catch (error: any) {
    Swal.close();

    const errorMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      "Something went wrong while assigning the package.";

    await Swal.fire({
      icon: "error",
      title: "Assignment Failed",
      text: errorMessage,
      confirmButtonText: "OK",
    });
  }
};
  if (!show) return null;

  return (
    <div
      className="modal fade show d-block"
      tabIndex={-1}
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div
        className="modal-dialog modal-xl modal-dialog-centered "
        style={{ minWidth: "450px", maxWidth: "700px" }}
      >
        <div className="modal-content border-0 rounded-3">
          {/* Header */}
          <div className="modal-header">
            <h4 className="fw-bold">Assign Bulk package</h4>

            <button
              type="button"
              className="btn-close"
              onClick={handleClose}
            ></button>
          </div>

          {/* Body */}
          <div className="modal-body">
            <div className="row gx-3">
              {/* Client Name */}
              <div className="col-md-12">
                <SearchableSelect
                  label="Brand Name"
                  name="account_name"
                  value={filterData.account_name}
                  options={projects.map((item: any) => ({
                    label: item.project_name,
                    value: item.project_id,
                  }))}
                  onChange={handleFieldDataChange}
                  required
                />
              </div> 
              <div className="col-md-12">
                <label
                  style={{
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "#333",
                    marginBottom: 6,
                    display: "block",
                  }}
                >
                  Package <sup className="text-danger">*</sup>
                </label>
                <Select
                  className="mb-1 border-0"
                  options={packageOptions}
                  projectId={
                    packageOptions.find(
                      (item) => item.projectId === filterData.package,
                    ) || null
                  }
                  onChange={(option: any) =>
                    handleFieldDataChange("package", option?.projectId)
                  }
                  components={{
                    Option: CustomMultilineOption,
                    SingleValue: CustomPackageSingleValue,
                  }}
                  styles={packageSelectStyles}
                  placeholder="Search or select a package..."
                  isClearable
                  isSearchable
                /> 
              </div> 
            </div>
          </div>

          {/* Footer Button */}
          <div className="modal-footer ">
            <button type="button" className="btn border" onClick={handleClose}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-danger px-4 ms-2"
              // onClick={handleClose}
              onClick={handleSave}
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignBulkPackage;